<template>
  <ContentSection>
    <Container indent="wide">
      <h2
        v-if="data.heading"
        class="default-component-title mb-7"
        v-html="$replaceHtmlTags(data.heading)"
      />

      <div
        class="mb-10 flex flex-wrap justify-between gap-6 sm:mb-12 sm:gap-10 lg:flex-nowrap"
      >
        <!-- FILTER RESPONSIBILITY AREAS -->
        <template
          v-if="
            data.filter.value === 'responsibility_areas' ||
            data.filter.value === 'all'
          "
        >
          <Filter
            :class="{ 'lg:w-2/3': data.filter.value === 'all' }"
            :terms="allowedResponsibilityAreas"
            :filtered="filter.responsibilityArea"
            :button-all-text="$t('all')"
            @filter="filter.responsibilityArea = $event"
          >
            {{ $t('filter.filter_for_responsibility_areas') }}
          </Filter>
        </template>

        <!-- FILTER LOCATIONS -->
        <template
          v-if="
            data.filter.value === 'locations' || data.filter.value === 'all'
          "
        >
          <!-- DESKTOP -->
          <Filter
            :class="{ 'lg:w-1/3': data.filter.value === 'all' }"
            class="hidden sm:block"
            :terms="allowedLocations"
            :filtered="filter.location"
            :button-all-text="$t('all')"
            @filter="filter.location = $event"
          >
            {{ $t('filter.filter_for_locations') }}
          </Filter>

          <!-- MOBILE -->
          <div class="relative min-h-[1.625rem] w-full sm:hidden">
            <TransitionExpand
              class="w-full overflow-hidden transition-all delay-200 duration-300 ease-in-out"
              :open="isShowingMore"
            >
              <Filter
                :terms="allowedLocations"
                :filtered="filter.location"
                :button-all-text="$t('all')"
                @filter="filter.location = $event"
              >
                {{ $t('filter.filter_for_locations') }}
              </Filter>
            </TransitionExpand>
            <transition name="fade">
              <button
                v-show="!isShowingMore"
                class="absolute left-0 top-0 flex items-center gap-2 text-base text-blue-800"
                @click="isShowingMore = true"
              >
                <PlusIcon class="w-2.5 shrink-0" />
                <span>{{ $t('show_more') }}</span>
              </button>
            </transition>
          </div>
        </template>
      </div>
    </Container>

    <!-- TEAM MEMBERS -->
    <Container>
      <ul
        :key="rerenderKey"
        class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 2xl:grid-cols-4 5xl:grid-cols-5"
      >
        <li v-for="member in teamMembers" :key="member.id">
          <TeamMemberCard :data="member" />
        </li>
      </ul>

      <!-- NO ENTRIES FOUND -->
      <transition name="fade">
        <div
          v-show="teamMembers.length === 0"
          class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
        >
          <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
          <p class="text-md text-blue-dark-800">
            {{ $t('filter.no_entries_found') }}
          </p>
        </div>
      </transition>
    </Container>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
})

const { locale } = useI18n()
const route = useRoute()
const params = useUrlSearchParams('history')
const teamMembers = ref([])
const locations = ref([])
const responsibilityAreas = ref([])

const isShowingMore = ref(false)

// POPULATE THE FILTER
let routeQueryFilter = route.query[`team-overview-${props.data.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

const filter = ref(
  routeQueryFilter || {
    responsibilityArea: null,
    location: null,
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`team-overview-${props.data.id}`] = JSON.stringify(newValue)
    fetchTeamMembers()
  },
  { deep: true }
)

watch(
  () => props.data,
  async () => {
    await fetchTeamMembers()
  },
  { deep: true }
)

// COMPUTED
const allowedResponsibilityAreas = computed(() => {
  // If no restriction is set, return all responsibility areas
  if (
    !Array.isArray(props.data.restricted_responsibility_areas) ||
    props.data.restricted_responsibility_areas.length === 0
  ) {
    return responsibilityAreas.value
  }
  // Only allow the responsibility areas that are set in the restriction
  return props.data.restricted_responsibility_areas
})

const allowedLocations = computed(() => {
  // If no restriction is set, return all responsibility areas
  if (
    !Array.isArray(props.data.restricted_locations) ||
    props.data.restricted_locations.length === 0
  ) {
    return locations.value
  }
  // Only allow the responsibility areas that are set in the restriction
  return props.data.restricted_locations
})

// FUNCTIONS
async function fetchTeamMembers() {
  let payload = await useAsyncGql({
    operation: 'TeamMembers',
    variables: {
      sort: 'order',
      filter: GenerateQueryFilter(),
    },
  })

  payload = payload.data.value?.entries

  if (!payload) {
    console.error('TeamMembers could not be fetched')
    return
  }

  teamMembers.value = payload.data
}

function GenerateQueryFilter() {
  const queryFilter = {
    site: locale.value,
  }

  // If the selection is set to manually, we want to filter by the selected id's
  if (props.data.selection.value === 'manually') {
    queryFilter.id = {
      in: props.data.team_members.map((member) => member.id),
    }
  }

  // Responsibility area
  if (filter.value.responsibilityArea) {
    queryFilter.responsibility_area = switchContainsOrIn(
      filter.value.responsibilityArea
    )
  } else {
    queryFilter.responsibility_area = switchContainsOrIn(
      props.data.restricted_responsibility_areas
    )
  }

  // Location
  if (filter.value.location) {
    queryFilter.location = switchContainsOrIn(filter.value.location)
  } else {
    queryFilter.location = switchContainsOrIn(props.data.restricted_locations)
  }

  return queryFilter
}

// INITIAL
let payload = await useAsyncGql({
  operation: 'TeamMembersAndTerms',
  variables: {
    sort: 'order',
    filter: GenerateQueryFilter(),
    filterLocale: {
      site: locale.value,
    },
  },
})

payload = payload.data?.value

if (!payload) {
  console.error('TeamMembers could not be fetched')
}

locations.value = payload?.locations?.data

responsibilityAreas.value = payload?.responsibility_areas?.data

teamMembers.value = payload?.entries.data

/*
  Rerender the overview if the filter is set in the url.
  his is needed because the ISR cache provides the data without the filter applied -> This results in a wrong data mix (e.g. image and name not corresponding)
  so wie need to rerender the overview with the filter applied.
*/
const rerenderKey = ref(0)
onMounted(() => {
  if (route.query[`team-overview-${props.data.id}`]) {
    rerenderKey.value++
  }
})
</script>
